import { createContext, useContext, useState, useEffect } from 'react';
// import Dexie from 'dexie';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../utils/extract-api-error';
import Cookies from 'js-cookie';

// Create context
const GuestAgentChatContext = createContext();

const isLocalhostDomain = () => {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
};

export const storeCookiesInParentDomain = ({ name, value }) => {
    const cookieOptions = {
        expires: 1, // 1 day — matches access token lifetime
        path: '/',
    };
    if (!isLocalhostDomain()) {
        Cookies.set(name, value, {
            ...cookieOptions,
            domain: 'leasehub.app',
        });
    } else {
        Cookies.set(name, value, cookieOptions);
    }
};

// Cookies set with a `domain` attribute can only be removed by a removal that
// specifies the same domain, so this must mirror storeCookiesInParentDomain.
export const removeCookieFromParentDomain = (name) => {
    if (!isLocalhostDomain()) {
        Cookies.remove(name, { path: '/', domain: 'leasehub.app' });
    } else {
        Cookies.remove(name, { path: '/' });
    }
};

// Context provider component
export const GuestAgentChatContextProvider = ({ children }) => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [guestAgent, setGuestAgent] = useState(null);

    // const [files, setFiles] = useState(initialFields);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlGuestAgent = urlParams.get('guest_agent');

        if (urlGuestAgent) {
            storeCookiesInParentDomain({ name: 'guest_agent', value: urlGuestAgent });
        }

        const storedUuid = urlGuestAgent || Cookies.get('guest_agent');

        console.log('Stored UUID:', storedUuid);
        if (storedUuid) {
            fetchGuestAgentData(storedUuid);
        }
    }, []);

    useEffect(() => {
        if (guestAgent && guestAgent?.uuid) {
            fetchGroupChat(guestAgent?.uuid);
        }
    }, [guestAgent]);

    useEffect(() => {
        if (selectedUser && guestAgent && guestAgent.uuid) {
            setMessages([]);
            getMessages();
        }
    }, [selectedUser]);

    const fetchGroupChat = async (uuid) => {
        try {
            const response = await fetch(`/api/chat/guest-agent/groups/${uuid}/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (response.ok) {
                const sortedGroups = Array.isArray(data)
                    ? [...data].sort(
                          (a, b) =>
                              new Date(b.createdAt || b.created_at || 0) -
                              new Date(a.createdAt || a.created_at || 0)
                      )
                    : [];
                setUsers(sortedGroups);
                return data;
            } else {
                toast.error(apiErrorMessage(data, 'Could not load chats.'));
            }
        } catch (error) {
            console.log(error);
            toast.error(apiErrorMessage(error, 'Could not load chats.'));
        }
    };

    const getMessages = async () => {
        try {
            // http://greenocean.localhost:3000/api/chat/history?groupId=b6ce7acf-ae26-4015-8f95-2318c90b87c3&member_id=1234567899&member_type=AGENT
            const response = await fetch(
                `/api/chat/history?groupId=${selectedUser?.id}&member_id=${guestAgent?.uuid}&member_type=GUEST_AGENT`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            const data = await response.json();
            if (response.ok) {
                setMessages(data.results);
            } else {
                toast.error(apiErrorMessage(data, 'Could not load messages.'));
            }
        } catch (error) {
            console.log(error);
            toast.error(apiErrorMessage(error, 'Could not load messages.'));
        }
    };

    const fetchGuestAgentData = async (storedUuid) => {
        try {
            const response = await fetch(`/api/agent/guest-agents/${storedUuid}/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (response.ok) {
                storeCookiesInParentDomain({ name: 'guest_agent', value: data.uuid });

                setGuestAgent(data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const sendMessage = async (message) => {
        const payload = {
            guest_agent_id: guestAgent?.uuid,
            message: message.message,
        };
        // When replying to an existing chat, always send group_id to target the correct group
        if (selectedUser?.id) {
            payload.group_id = selectedUser.id;
        } else {
            payload.listing_id = message.listing_id;
        }
        try {
            const response = await fetch(`/api/agent/guest-agent/message/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (response.ok) {
                if (!selectedUser?.listingId && message.listing_id) {
                    const groups = fetchGroupChat(guestAgent?.uuid);

                    if (groups.length !== 0 && message.listing_id) {
                        const group = groups.filter(
                            (chatGroup) => chatGroup.listingId === message.listing_id
                        );
                        if (group.length !== 0) {
                            setSelectedUser(group[0]);
                        }
                    }
                } else {
                    getMessages();
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const agentUUID = guestAgent?.uuid;

    const contextValue = {
        users,
        guestAgent,
        setGuestAgent,
        agentUUID,
        selectedUser,
        setSelectedUser,
        messages,
        setMessages,
        sendMessage,
    };

    return (
        <GuestAgentChatContext.Provider value={contextValue}>
            {children}
        </GuestAgentChatContext.Provider>
    );
};

// Custom hook to use the context
export const useGuestAgentChat = () => {
    const context = useContext(GuestAgentChatContext);
    if (!context) {
        throw new Error('useGuestAgentChat must be used within a GuestAgentChatContextProvider');
    }
    return context;
};
